import React, { useCallback, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { User, Menu } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { MobileMenu } from './MobileMenu';

const NAV_ITEMS = [
  { id: 'framework', label: 'Framework' },
  { id: 'audiences', label: 'Who We Help' },
  { id: 'programs', label: 'Programs' },
  { id: 'pranic-healing', label: 'Pranic Healing', to: '/services/pranic-healing' },
  { id: 'why-zeeva', label: 'Why Zeeva' },
  { id: 'testimonials', label: 'Stories' },
  { id: 'contact', label: 'Contact' },
];

const MOBILE_ITEMS = [...NAV_ITEMS, { id: 'blog', label: 'Blog', to: '/blog' }];

const AccountMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const initial = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase();

  const handleLogout = async () => {
    setOpen(false);
    navigate('/');
    await logout();
  };

  return (
    <div style={{ position: 'relative' }} data-testid="header-account-menu">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="header-account-btn"
        aria-label="Account menu"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #1DB954, #FFD700)',
          color: '#0F1524',
          fontWeight: 800,
          fontSize: '0.95rem',
          border: 'none',
          cursor: 'pointer',
          marginLeft: '0.75rem',
          flexShrink: 0,
        }}
      >
        {user?.picture ? (
          <img
            src={user.picture}
            alt={user.name}
            style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
          />
        ) : (
          initial
        )}
      </button>
      {open && (
        <div
          data-testid="header-account-dropdown"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            minWidth: '220px',
            padding: '0.5rem',
            borderRadius: '14px',
            background: 'rgba(20, 27, 46, 0.98)',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
            zIndex: 999,
          }}
        >
          <div style={{ padding: '0.75rem 0.85rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <p style={{ color: 'white', fontSize: '0.9rem', fontWeight: 600 }}>
              {user?.name || 'Signed in'}
            </p>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem' }}>
              {user?.email}
            </p>
          </div>
          <Link
            to="/account"
            onClick={() => setOpen(false)}
            data-testid="header-account-link"
            style={{
              display: 'block',
              padding: '0.7rem 0.85rem',
              color: 'rgba(255,255,255,0.85)',
              fontSize: '0.9rem',
              textDecoration: 'none',
              borderRadius: '10px',
            }}
          >
            Your account
          </Link>
          <button
            onClick={handleLogout}
            data-testid="header-logout-btn"
            style={{
              width: '100%',
              textAlign: 'left',
              padding: '0.7rem 0.85rem',
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.85)',
              fontSize: '0.9rem',
              cursor: 'pointer',
              borderRadius: '10px',
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, loading, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const handleMobileLogout = async () => {
    setMenuOpen(false);
    navigate('/');
    await logout();
  };

  const scrollToSection = useCallback(
    (id) => {
      if (location.pathname !== '/') {
        navigate(`/#${id}`);
        return;
      }
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [location.pathname, navigate]
  );

  return (
    <header className="network-header" data-testid="site-header">
      <div className="nav-wrapper">
        <Link
          to="/"
          className="network-logo-link"
          data-testid="header-logo-link"
        >
          <img
            src="/images/zeeva-logo.png"
            alt="ZEEVA Life"
            className="network-logo-img"
          />
          <span className="network-logo-text">ZEEVA</span>
        </Link>

        <nav className="network-nav" aria-label="Primary navigation">
          {NAV_ITEMS.map((item) => item.to ? (
            <Link
              key={`nav-${item.id}`}
              to={item.to}
              className="network-nav-link"
              data-testid={`nav-link-${item.id}`}
            >
              {item.label}
            </Link>
          ) : (
            <a
              key={`nav-${item.id}`}
              onClick={() => scrollToSection(item.id)}
              className="network-nav-link"
              data-testid={`nav-link-${item.id}`}
            >
              {item.label}
            </a>
          ))}
          <Link
            to="/blog"
            className="network-nav-link"
            data-testid="nav-link-blog"
          >
            Blog
          </Link>

          {!loading && !isAuthenticated && (
            <Link
              to="/login"
              className="network-nav-link"
              data-testid="nav-link-signin"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            >
              <User size={16} aria-hidden="true" />
              Sign In
            </Link>
          )}

          <Link
            to="/assessment"
            className="btn-cta nav-assessment-cta"
            data-testid="nav-cta-assessment"
            style={{
              marginLeft: '0.75rem',
              padding: '0.55rem 1.25rem',
              fontSize: '0.85rem',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Start Your Assessment
          </Link>

          {!loading && isAuthenticated && <AccountMenu />}
        </nav>

        <button
          type="button"
          className="mm-toggle"
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
          aria-expanded={menuOpen}
          data-testid="mobile-menu-toggle-btn"
        >
          <Menu size={22} />
        </button>
        <MobileMenu
          open={menuOpen}
          onClose={closeMenu}
          items={MOBILE_ITEMS}
          onSection={scrollToSection}
          onLogout={handleMobileLogout}
        />
      </div>
    </header>
  );
};

export default Header;
